import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Suspense, lazy } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { AlertProvider } from './context/AlertContext';
import { ThemeProvider } from './context/ThemeContext';
import { DensityProvider } from './components/layout/DensityProvider';

// Critical - loaded immediately (only Login)
import LoginPage from './pages/LoginPage';

// Components - loaded immediately (lightweight)
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// All pages - lazy loaded
const LazyDashboard = lazy(() => import('./pages/DashboardPage'));
const LazyTextoTeorico = lazy(() => import('./pages/TextoTeoricoPage'));
const LazyRoteiroVideo = lazy(() => import('./pages/RoteiroVideoPage'));
const LazyQuestoesFechadas = lazy(() => import('./pages/QuestoesFechadasPage'));
const LazyItems = lazy(() => import('./pages/ItemsPage'));
const LazyUserPage = lazy(() => import('./pages/UserPage'));
const LazyProjectPage = lazy(() => import('./pages/ProjectPage'));
const LazyAnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const LazyPromptPage = lazy(() => import('./pages/PromptPage'));
const LazyBiomePage = lazy(() => import('./pages/BiomePage'));
const LazyCharacterPage = lazy(() => import('./pages/CharacterPage'));
const LazySkillPage = lazy(() => import('./pages/SkillPage'));
const LazyHabilidadeSaebPage = lazy(() => import('./pages/HabilidadeSaebPage'));
const LazyHabilidadeEnemPage = lazy(() => import('./pages/HabilidadeEnemPage'));
const LazyKnowledgeAreaPage = lazy(() => import('./pages/KnowledgeAreaPage'));
const LazyRagPage = lazy(() => import('./pages/RagPage'));
const LazyRoteiroVideoArquivoPage = lazy(() => import('./pages/RoteiroVideoArquivoPage'));
const LazyQuestoesFechadasFormPage = lazy(() => import('./pages/QuestoesFechadasFormPage'));
const LazyQuestoesFechadasComArquivoPage = lazy(() => import('./pages/QuestoesFechadasComArquivoPage'));
const LazyQuestoesAbertasPage = lazy(() => import('./pages/QuestoesAbertasPage'));
const LazyCriacaoCompletaPage = lazy(() => import('./pages/CriacaoCompletaPage'));
const LazyLogsAdmin = lazy(() => import('./pages/LogsAdmin'));
const LazyOrganizationAdminPage = lazy(() => import('./pages/OrganizationAdminPage'));
const LazyBillingPage = lazy(() => import('./pages/BillingPage'));
const LazyExecutivePage = lazy(() => import('./pages/ExecutivePage'));
const LazyOrganizationPage = lazy(() => import('./pages/OrganizationPage'));
const LazyOrientacaoPedagogicaPage = lazy(() => import('./pages/OrientacaoPedagogicaPage'));
const LazyTextoAlternativoPage = lazy(() => import('./pages/TextoAlternativoPage'));
const LazyQuestoesInepUnificadaPage = lazy(() => import('./pages/QuestoesInepUnificadaPage'));
const LazyQuestoesSomosConectePage = lazy(() => import('./pages/QuestoesSomosConectePage'));
const LazyQuestoesMultiplaEscolhaPage = lazy(() => import('./pages/QuestoesMultiplaEscolhaPage'));
const LazyQuestoesFlexiveisPage = lazy(() => import('./pages/QuestoesFlexiveisPage'));
const LazyNotificationManagementPage = lazy(() => import('./pages/NotificationManagementPage'));
const LazyProfilePage = lazy(() => import('./pages/ProfilePage'));
const LazyGeracaoImagemPage = lazy(() => import('./pages/GeracaoImagemPage'));
const LazyTestePromptsPage = lazy(() => import('./pages/TestePromptsPage'));
const LazyCustomModulesPage = lazy(() => import('./pages/CustomModules'));
const LazyCustomModuleGenerationPage = lazy(() => import('./pages/CustomModuleGeneration'));
const LazyDiarioExpedicaoPage = lazy(() => import('./pages/DiarioExpedicaoPage'));
const LazyComoAprendemosPage = lazy(() => import('./pages/ComoAprendemosPage'));
const LazyLLMConfigPage = lazy(() => import('./pages/LLMConfigPage'));

const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="400px"
  >
    <CircularProgress size={24} />
  </Box>
);

const App = () => {
  return (
    <ThemeProvider>
      <CssBaseline />
      <DensityProvider>
        <AlertProvider>
          <AuthProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                <Route path="/" element={<div style={{ display: 'none' }} />} />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyDashboard />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/usuarios/*"
                  element={
                    <ProtectedRoute
                      requiredCargo={['super_admin', 'org_admin']}
                    >
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyUserPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/projetos/*"
                  element={
                    <ProtectedRoute requiredPermission="gerenciarProjetos">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyProjectPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/prompts/*"
                  element={
                    <ProtectedRoute requiredPermission="gerenciarPrompts">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyPromptPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/biomas/*"
                  element={
                    <ProtectedRoute requiredPermission="gerenciarProjetos">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyBiomePage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/personagens"
                  element={
                    <ProtectedRoute requiredPermission="gerenciarProjetos">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyCharacterPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/habilidades"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazySkillPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/habilidades-saeb"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyHabilidadeSaebPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/habilidades-enem"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyHabilidadeEnemPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/areas"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyKnowledgeAreaPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* 💰 ECONOMIA: rota da Base de Conhecimento (RAG/AWS Bedrock).
                    Só fica ativa se REACT_APP_RAG_ENABLED === 'true'.
                    Desativada => redireciona para o dashboard. Nada foi apagado. */}
                <Route
                  path="/rag"
                  element={
                    process.env.REACT_APP_RAG_ENABLED === 'true' ? (
                      <ProtectedRoute requiredPermission="gerenciarRAG">
                        <Layout>
                          <Suspense fallback={<LoadingFallback />}>
                            <LazyRagPage />
                          </Suspense>
                        </Layout>
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/dashboard" />
                    )
                  }
                />

                <Route
                  path="/textos-teoricos"
                  element={
                    <ProtectedRoute requiredPermission="gerarTextoTeorico">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyTextoTeorico />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/roteiros-video"
                  element={
                    <ProtectedRoute requiredPermission="gerarRoteiroVideo">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyRoteiroVideo />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/roteiros-video-arquivo"
                  element={
                    <ProtectedRoute requiredPermission="gerarRoteiroVideo">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyRoteiroVideoArquivoPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-fechadas"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesFechadas">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesFechadas />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-fechadas-form"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesFechadas">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesFechadasFormPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-fechadas-com-arquivo"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesFechadas">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesFechadasComArquivoPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-abertas"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesAbertas">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesAbertasPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/orientacao-pedagogica"
                  element={
                    <ProtectedRoute requiredPermission="gerarOrientacaoPedagogica">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyOrientacaoPedagogicaPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/texto-alternativo"
                  element={
                    <ProtectedRoute requiredPermission={["gerarTextoAlternativo", "gerarTextoTeorico"]}>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyTextoAlternativoPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-inep"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesInep">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesInepUnificadaPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-somos-conecte"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesFechadas">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesSomosConectePage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-multipla-escolha"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesFechadas">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesMultiplaEscolhaPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questoes-flexiveis"
                  element={
                    <ProtectedRoute requiredPermission="gerarQuestoesAbertas">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyQuestoesFlexiveisPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/itens/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyItems />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/criacao-completa"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyCriacaoCompletaPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/logs"
                  element={
                    <ProtectedRoute requiredPermission="gerenciarUsuarios">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyLogsAdmin />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin-organizacao"
                  element={
                    <ProtectedRoute requiredCargo="org_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyOrganizationAdminPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/cobranca"
                  element={
                    <ProtectedRoute requiredCargo="org_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyBillingPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute requiredCargo="super_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyAnalyticsPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/executive"
                  element={
                    <ProtectedRoute requiredCargo="super_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyExecutivePage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/organizacoes"
                  element={
                    <ProtectedRoute requiredCargo="super_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyOrganizationPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/notificacoes"
                  element={
                    <ProtectedRoute requiredCargo="super_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyNotificationManagementPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/perfil"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyProfilePage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/geracao-imagem"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyGeracaoImagemPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/teste-prompts"
                  element={
                    <ProtectedRoute requiredPermission="testarPrompts">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyTestePromptsPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/modulos-customizados"
                  element={
                    <ProtectedRoute requiredCargo="super_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyCustomModulesPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/diario-expedicao"
                  element={
                    <ProtectedRoute requiredOrganization="Texto e Forma Conteúdo Educacional">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyDiarioExpedicaoPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/como-aprendemos"
                  element={
                    <ProtectedRoute requiredOrganization="Texto e Forma Conteúdo Educacional">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyComoAprendemosPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/llm-config"
                  element={
                    <ProtectedRoute requiredCargo="org_admin">
                      <Layout>
                        <Suspense fallback={<LoadingFallback />}>
                          <LazyLLMConfigPage />
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </AlertProvider>
      </DensityProvider>
    </ThemeProvider>
  );
};

export default App;
